import {
  DM2016_CALCULATION_VERSION,
  Dm2016CalculationInputError,
  calculateDm2016Service,
  summarizeDm2016Compensation,
  type Dm2016CompensationSummaryResult,
  type Dm2016QDefinition,
  type Dm2016ServiceCalculationResult,
} from "./dm2016-calculation.js";
import { canonicalJson, sha256Hex, type JsonValue } from "./canonical.js";
import { CalcDecimal, decimalString } from "./decimal.js";
import {
  DM2016_FOOTNOTE_RESOLVER_VERSION,
  resolveDm2016FootnoteEffects,
  type Dm2016FootnoteContext,
  type Dm2016FootnoteResolution,
} from "./dm2016-footnotes.js";

export type Dm2016AtomicScope =
  | "EDILIZIA"
  | "STRUTTURE_S01_S03"
  | "STRUTTURE_S02_S04_S05_S06"
  | "IMPIANTI_IA"
  | "IMPIANTI_IB"
  | "VIABILITA"
  | "IDRAULICA"
  | "TIC"
  | "AGRICOLTURA_AMBIENTE"
  | "TERRITORIO_URBANISTICA";

export type Dm2016ReferenceInterpretationMode =
  | "printed_source_strict"
  | "professionearchitetto_v0_74";

export interface Dm2016ReferenceCalculationInput {
  readonly z1: unknown;
  readonly z2Matrix: unknown;
  readonly selectionRules: unknown;
  readonly destinationCode: string;
  readonly serviceCode: string;
  readonly valueV: string;
  readonly inhabitants?: string;
  readonly multiplier?: string;
  readonly interpretationMode: Dm2016ReferenceInterpretationMode;
}

export interface Dm2016ReferenceResolution {
  readonly destinationCode: string;
  readonly categoryCode: string;
  readonly atomicScope: Dm2016AtomicScope;
  readonly complexityG: string;
  readonly serviceCode: string;
  readonly qDefinition: Dm2016QDefinition;
  readonly selectorKind: "work_value" | "population";
  readonly interpretationMode: Dm2016ReferenceInterpretationMode;
  readonly warnings: readonly string[];
}

export interface Dm2016ReferenceCalculationResult {
  readonly resolution: Dm2016ReferenceResolution;
  readonly calculation: Dm2016ServiceCalculationResult;
}

export interface Dm2016ReferenceRulesetIdentity {
  readonly rulesetId: string;
  readonly rulesetVersion: string;
  readonly sourceSha256: string;
}

export interface HashedDm2016ReferenceCalculationResult {
  readonly engineVersion: typeof DM2016_CALCULATION_VERSION;
  readonly rulesetId: string;
  readonly rulesetVersion: string;
  readonly sourceSha256: string;
  readonly professionalUseAllowed: false;
  readonly certifiedEquoCompenso: false;
  readonly releaseStatus: "local_unverified";
  readonly normalizedInput: {
    readonly destinationCode: string;
    readonly serviceCode: string;
    readonly valueV: string;
    readonly inhabitants: string | null;
    readonly multiplier: string;
    readonly interpretationMode: Dm2016ReferenceInterpretationMode;
  };
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly reference: Dm2016ReferenceCalculationResult;
}

export interface HashedDm2016FootnoteAwareReferenceCalculationResult {
  readonly engineVersion: typeof DM2016_CALCULATION_VERSION;
  readonly rulesetId: string;
  readonly rulesetVersion: string;
  readonly sourceSha256: string;
  readonly professionalUseAllowed: false;
  readonly certifiedEquoCompenso: false;
  readonly releaseStatus: "local_unverified";
  readonly normalizedInput: HashedDm2016ReferenceCalculationResult["normalizedInput"] & {
    readonly footnoteContext: {
      readonly procurementModel: Dm2016FootnoteContext["procurementModel"] | null;
      readonly tenderBaseProject: boolean | null;
      readonly publicWorksConcession: boolean | null;
      readonly planning: NonNullable<Dm2016FootnoteContext["planning"]> | null;
      readonly variationAbsoluteIncrease: string | null;
      readonly variationAbsoluteDecrease: string | null;
      readonly newlyDesignedWorksGrossValue: string | null;
      readonly collaudoInCorsoDOpera: boolean | null;
      readonly energyDiagnosisDocumentationAvailable: boolean | null;
    };
    readonly footnoteResolverVersion: typeof DM2016_FOOTNOTE_RESOLVER_VERSION;
  };
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly footnoteResolution: Dm2016FootnoteResolution;
  readonly reference: Dm2016ReferenceCalculationResult;
}

export interface Dm2016ReferenceBundleServiceInput {
  readonly serviceCode: string;
  readonly valueV?: string;
  readonly inhabitants?: string;
  readonly multiplier?: string;
}

export interface HashedDm2016FootnoteAwareReferenceBundleResult {
  readonly engineVersion: typeof DM2016_CALCULATION_VERSION;
  readonly rulesetId: string;
  readonly rulesetVersion: string;
  readonly sourceSha256: string;
  readonly professionalUseAllowed: false;
  readonly certifiedEquoCompenso: false;
  readonly releaseStatus: "local_unverified";
  readonly normalizedInput: {
    readonly destinationCode: string;
    readonly services: readonly {
      readonly serviceCode: string;
      readonly valueV: string;
      readonly inhabitants: string | null;
      readonly multiplier: string;
    }[];
    readonly interpretationMode: Dm2016ReferenceInterpretationMode;
    readonly footnoteContext:
      HashedDm2016FootnoteAwareReferenceCalculationResult["normalizedInput"]["footnoteContext"];
    readonly footnoteResolverVersion: typeof DM2016_FOOTNOTE_RESOLVER_VERSION;
  };
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly footnoteResolution: Dm2016FootnoteResolution;
  readonly references: readonly Dm2016ReferenceCalculationResult[];
  readonly compensationSummary: Dm2016CompensationSummaryResult;
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown, label: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Dm2016CalculationInputError(`${label} must be an object`);
  }
  return value as UnknownRecord;
}

function array(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new Dm2016CalculationInputError(`${label} must be an array`);
  }
  return value;
}

function string(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Dm2016CalculationInputError(`${label} must be a non-empty string`);
  }
  return value;
}

export function dm2016AtomicScopeForDestinationCode(
  destinationCode: string,
): Dm2016AtomicScope {
  if (/^E\.\d{2}$/.test(destinationCode)) return "EDILIZIA";
  if (/^S\.(?:01|03)$/.test(destinationCode)) return "STRUTTURE_S01_S03";
  if (/^S\.(?:02|04|05|06)$/.test(destinationCode)) {
    return "STRUTTURE_S02_S04_S05_S06";
  }
  if (/^IA\.\d{2}$/.test(destinationCode)) return "IMPIANTI_IA";
  if (/^IB\.\d{2}$/.test(destinationCode)) return "IMPIANTI_IB";
  if (/^V\.\d{2}$/.test(destinationCode)) return "VIABILITA";
  if (/^D\.\d{2}$/.test(destinationCode)) return "IDRAULICA";
  if (/^T\.\d{2}$/.test(destinationCode)) return "TIC";
  if (/^P\.\d{2}$/.test(destinationCode)) return "AGRICOLTURA_AMBIENTE";
  if (/^U\.\d{2}$/.test(destinationCode)) return "TERRITORIO_URBANISTICA";
  throw new Dm2016CalculationInputError(
    `Destination code is not mapped to an atomic scope: ${destinationCode}`,
  );
}

function resolveDestination(z1: unknown, destinationCode: string): {
  categoryCode: string;
  complexityG: string;
} {
  const root = record(z1, "Z-1 corpus");
  for (const categoryValue of array(root.categories, "Z-1 categories")) {
    const category = record(categoryValue, "Z-1 category");
    for (const destinationValue of array(
      category.destinations,
      "Z-1 destinations",
    )) {
      const destination = record(destinationValue, "Z-1 destination");
      if (destination.code === destinationCode) {
        return {
          categoryCode: string(category.code, "Z-1 category code"),
          complexityG: string(destination.g, "Z-1 G"),
        };
      }
    }
  }
  throw new Dm2016CalculationInputError(
    `Destination is not present in Z-1: ${destinationCode}`,
  );
}

function resolveServiceVariants(
  z2Matrix: unknown,
  serviceCode: string,
  atomicScope: Dm2016AtomicScope,
): { variants: UnknownRecord[]; atomicScopeIndex: number } {
  const root = record(z2Matrix, "Z-2 matrix");
  const scopes = array(root.atomic_scope_order, "Z-2 atomic scope order");
  const atomicScopeIndex = scopes.indexOf(atomicScope);
  if (atomicScopeIndex < 0) {
    throw new Dm2016CalculationInputError(
      `Atomic scope is missing from Z-2: ${atomicScope}`,
    );
  }
  const serviceValue = array(root.services, "Z-2 services").find(
    (candidate) => record(candidate, "Z-2 service").code === serviceCode,
  );
  if (serviceValue === undefined) {
    throw new Dm2016CalculationInputError(
      `Service is not present in Z-2: ${serviceCode}`,
    );
  }
  const service = record(serviceValue, "Z-2 service");
  return {
    variants: array(service.variants, "Z-2 service variants").map((variant) =>
      record(variant, "Z-2 variant")
    ),
    atomicScopeIndex,
  };
}

function qAtScope(variant: UnknownRecord, atomicScopeIndex: number): string {
  const values = array(variant.q, "Z-2 Q vector");
  const value = values[atomicScopeIndex];
  if (value === null || value === undefined) {
    throw new Dm2016CalculationInputError(
      `Service ${String(variant.variant_id)} is not applicable to the selected destination`,
    );
  }
  return string(value, `Q for ${String(variant.variant_id)}`);
}

function resolveQDefinition(
  variants: UnknownRecord[],
  atomicScopeIndex: number,
  atomicScope: Dm2016AtomicScope,
  selectionRules: unknown,
  serviceCode: string,
  interpretationMode: Dm2016ReferenceInterpretationMode,
): {
  qDefinition: Dm2016QDefinition;
  selectorKind: "work_value" | "population";
  warnings: string[];
} {
  if (variants.length === 1) {
    return {
      qDefinition: {
        kind: "single",
        q: qAtScope(variants[0]!, atomicScopeIndex),
      },
      selectorKind: "work_value",
      warnings: [],
    };
  }

  const rulesRoot = record(selectionRules, "Z-2 selection rules");
  const ruleValue = array(
    rulesRoot.selection_rules,
    "Z-2 selection rules list",
  ).find((candidate) =>
    record(candidate, "Z-2 selection rule").service_code === serviceCode
  );
  if (ruleValue === undefined) {
    throw new Dm2016CalculationInputError(
      `Selection rule is missing for multi-variant service: ${serviceCode}`,
    );
  }
  const rule = record(ruleValue, "Z-2 selection rule");
  const variantsById = new Map(
    variants.map((variant) => [
      string(variant.variant_id, "Z-2 variant ID"),
      variant,
    ]),
  );
  const ruleVariants = array(rule.variants, "Z-2 rule variants");
  const bands = ruleVariants.map((ruleVariantValue) => {
    const ruleVariant = record(ruleVariantValue, "Z-2 rule variant");
    const variantId = string(ruleVariant.variant_id, "Z-2 rule variant ID");
    const matrixVariant = variantsById.get(variantId);
    if (matrixVariant === undefined) {
      throw new Dm2016CalculationInputError(
        `Rule variant is missing from Z-2 matrix: ${variantId}`,
      );
    }
    const upperBound = ruleVariant.upper_bound;
    if (upperBound !== null && typeof upperBound !== "string") {
      throw new Dm2016CalculationInputError(
        `Upper bound must be a string or null: ${variantId}`,
      );
    }
    const q = qAtScope(matrixVariant, atomicScopeIndex);
    const useCniQcI09Correction =
      interpretationMode === "professionearchitetto_v0_74"
      && serviceCode === "QcI.09"
      && variantId === "QcI.09#2"
      && (
        atomicScope === "EDILIZIA"
        || atomicScope === "STRUTTURE_S01_S03"
        || atomicScope === "STRUTTURE_S02_S04_S05_S06"
      )
      && new CalcDecimal(q).eq("0.012");
    return {
      upperBound,
      q,
      ...(useCniQcI09Correction ? { qApplied: "0.12" } : {}),
    };
  });
  const printedInterpolation = string(
    rule.interpolation_rule,
    "Z-2 interpolation rule",
  ).startsWith("LINEAR_INTERPOLATION_PRINTED");
  const selectorBasis = string(rule.selector_basis, "Z-2 selector basis");
  const warnings: string[] = [];
  if (
    interpretationMode === "professionearchitetto_v0_74"
    && !printedInterpolation
    && bands.length > 2
  ) {
    warnings.push(
      "Market compatibility interpolates Q although no interpolation footnote is printed for this service.",
    );
  }
  if (String(rule.execution_status).startsWith("blocked")) {
    warnings.push(
      `Selection rule ${serviceCode} is still blocked by the normative release gate.`,
    );
  }

  return {
    qDefinition: {
      kind: "progressive_bands",
      interpolateActiveBoundedBand:
        interpretationMode === "professionearchitetto_v0_74"
        || printedInterpolation,
      ...(printedInterpolation && interpretationMode === "printed_source_strict"
        ? { interpolatedQDecimalPlaces: 3 }
        : {}),
      bands,
    },
    selectorKind: selectorBasis === "POPULATION_INHABITANTS"
      ? "population"
      : "work_value",
    warnings,
  };
}

export function calculateDm2016ReferenceCaseForVerification(
  input: Dm2016ReferenceCalculationInput,
): Dm2016ReferenceCalculationResult {
  const destination = resolveDestination(input.z1, input.destinationCode);
  const atomicScope = dm2016AtomicScopeForDestinationCode(input.destinationCode);
  const { variants, atomicScopeIndex } = resolveServiceVariants(
    input.z2Matrix,
    input.serviceCode,
    atomicScope,
  );
  const resolvedQ = resolveQDefinition(
    variants,
    atomicScopeIndex,
    atomicScope,
    input.selectionRules,
    input.serviceCode,
    input.interpretationMode,
  );
  if (resolvedQ.selectorKind === "population" && input.inhabitants === undefined) {
    throw new Dm2016CalculationInputError(
      `Inhabitants are required for ${input.serviceCode}`,
    );
  }
  if (resolvedQ.selectorKind === "work_value" && input.inhabitants !== undefined) {
    throw new Dm2016CalculationInputError(
      `Inhabitants are not applicable to ${input.serviceCode}`,
    );
  }
  const resolution: Dm2016ReferenceResolution = {
    destinationCode: input.destinationCode,
    categoryCode: destination.categoryCode,
    atomicScope,
    complexityG: destination.complexityG,
    serviceCode: input.serviceCode,
    qDefinition: resolvedQ.qDefinition,
    selectorKind: resolvedQ.selectorKind,
    interpretationMode: input.interpretationMode,
    warnings: resolvedQ.warnings,
  };
  const calculation = calculateDm2016Service({
    serviceCode: input.serviceCode,
    valueV: input.valueV,
    complexityG: destination.complexityG,
    qDefinition: resolvedQ.qDefinition,
    selector: resolvedQ.selectorKind === "population"
      ? { kind: "population", inhabitants: input.inhabitants! }
      : { kind: "work_value" },
    ...(input.multiplier === undefined ? {} : { multiplier: input.multiplier }),
  });
  return Object.freeze({ resolution: Object.freeze(resolution), calculation });
}

export async function calculateDm2016ReferenceCaseWithHashes(
  input: Dm2016ReferenceCalculationInput & {
    readonly identity: Dm2016ReferenceRulesetIdentity;
  },
): Promise<HashedDm2016ReferenceCalculationResult> {
  if (
    input.identity.rulesetId.length === 0
    || input.identity.rulesetVersion.length === 0
    || !/^[a-f0-9]{64}$/.test(input.identity.sourceSha256)
  ) {
    throw new Dm2016CalculationInputError(
      "D.M. 2016 ruleset identity is invalid",
    );
  }
  const reference = calculateDm2016ReferenceCaseForVerification(input);
  const normalizedInput = Object.freeze({
    destinationCode: reference.resolution.destinationCode,
    serviceCode: reference.resolution.serviceCode,
    valueV: reference.calculation.valueV,
    inhabitants: reference.resolution.selectorKind === "population"
      ? decimalString(new CalcDecimal(input.inhabitants!))
      : null,
    multiplier: reference.calculation.multiplier,
    interpretationMode: reference.resolution.interpretationMode,
  });
  const identity = {
    rulesetId: input.identity.rulesetId,
    rulesetVersion: input.identity.rulesetVersion,
    sourceSha256: input.identity.sourceSha256,
  };
  const inputSha256 = await sha256Hex(canonicalJson({
    ...identity,
    ...normalizedInput,
  } as unknown as JsonValue));
  const resultForHash = {
    engineVersion: DM2016_CALCULATION_VERSION,
    ...identity,
    professionalUseAllowed: false as const,
    certifiedEquoCompenso: false as const,
    releaseStatus: "local_unverified" as const,
    normalizedInput,
    inputSha256,
    reference,
  } as const;
  const resultSha256 = await sha256Hex(
    canonicalJson(resultForHash as unknown as JsonValue),
  );

  return Object.freeze({ ...resultForHash, resultSha256 });
}

function normalizedOptionalDecimal(value: string | undefined): string | null {
  return value === undefined ? null : decimalString(new CalcDecimal(value));
}

function normalizeFootnoteContext(
  context: Dm2016FootnoteContext,
): HashedDm2016FootnoteAwareReferenceCalculationResult["normalizedInput"]["footnoteContext"] {
  return Object.freeze({
    procurementModel: context.procurementModel ?? null,
    tenderBaseProject: context.tenderBaseProject ?? null,
    publicWorksConcession: context.publicWorksConcession ?? null,
    planning: context.planning === undefined
      ? null
      : Object.freeze({ ...context.planning }),
    variationAbsoluteIncrease: normalizedOptionalDecimal(context.variationAbsoluteIncrease),
    variationAbsoluteDecrease: normalizedOptionalDecimal(context.variationAbsoluteDecrease),
    newlyDesignedWorksGrossValue: normalizedOptionalDecimal(context.newlyDesignedWorksGrossValue),
    collaudoInCorsoDOpera: context.collaudoInCorsoDOpera ?? null,
    energyDiagnosisDocumentationAvailable:
      context.energyDiagnosisDocumentationAvailable ?? null,
  });
}

export async function calculateDm2016FootnoteAwareReferenceCaseWithHashes(
  input: Dm2016ReferenceCalculationInput & {
    readonly identity: Dm2016ReferenceRulesetIdentity;
    readonly footnoteContext: Dm2016FootnoteContext;
  },
): Promise<HashedDm2016FootnoteAwareReferenceCalculationResult> {
  const footnoteResolution = resolveDm2016FootnoteEffects({
    services: [{ serviceCode: input.serviceCode, valueV: input.valueV }],
    context: input.footnoteContext,
  });
  const resolvedService = footnoteResolution.services[0];
  if (resolvedService === undefined) {
    throw new Dm2016CalculationInputError("D.M. 2016 footnote resolution is empty");
  }
  const effectiveMultiplier = decimalString(
    new CalcDecimal(input.multiplier ?? "1").times(resolvedService.multiplier),
  );
  const raw = await calculateDm2016ReferenceCaseWithHashes({
    ...input,
    valueV: resolvedService.valueV,
    multiplier: effectiveMultiplier,
  });
  const normalizedInput = Object.freeze({
    ...raw.normalizedInput,
    footnoteContext: normalizeFootnoteContext(input.footnoteContext),
    footnoteResolverVersion: DM2016_FOOTNOTE_RESOLVER_VERSION,
  });
  const identity = {
    rulesetId: raw.rulesetId,
    rulesetVersion: raw.rulesetVersion,
    sourceSha256: raw.sourceSha256,
  };
  const inputSha256 = await sha256Hex(canonicalJson({
    ...identity,
    ...normalizedInput,
  } as unknown as JsonValue));
  const resultForHash = {
    engineVersion: raw.engineVersion,
    ...identity,
    professionalUseAllowed: false as const,
    certifiedEquoCompenso: false as const,
    releaseStatus: "local_unverified" as const,
    normalizedInput,
    inputSha256,
    footnoteResolution,
    reference: raw.reference,
  } as const;
  const resultSha256 = await sha256Hex(
    canonicalJson(resultForHash as unknown as JsonValue),
  );
  return Object.freeze({ ...resultForHash, resultSha256 });
}

export async function calculateDm2016FootnoteAwareReferenceBundleWithHashes(
  input: {
    readonly z1: unknown;
    readonly z2Matrix: unknown;
    readonly selectionRules: unknown;
    readonly destinationCode: string;
    readonly services: readonly Dm2016ReferenceBundleServiceInput[];
    readonly interpretationMode: Dm2016ReferenceInterpretationMode;
    readonly footnoteContext: Dm2016FootnoteContext;
    readonly identity: Dm2016ReferenceRulesetIdentity;
  },
): Promise<HashedDm2016FootnoteAwareReferenceBundleResult> {
  if (
    input.identity.rulesetId.length === 0
    || input.identity.rulesetVersion.length === 0
    || !/^[a-f0-9]{64}$/.test(input.identity.sourceSha256)
  ) {
    throw new Dm2016CalculationInputError(
      "D.M. 2016 ruleset identity is invalid",
    );
  }
  if (input.services.length > 96) {
    throw new Dm2016CalculationInputError(
      "D.M. 2016 service bundle must not contain more than 96 services",
    );
  }
  const footnoteResolution = resolveDm2016FootnoteEffects({
    services: input.services.map((service) => ({
      serviceCode: service.serviceCode,
      ...(service.valueV === undefined ? {} : { valueV: service.valueV }),
    })),
    context: input.footnoteContext,
  });
  const sourceServices = new Map(
    input.services.map((service) => [service.serviceCode, service] as const),
  );
  const references = footnoteResolution.services.map((resolvedService) => {
    const sourceService = sourceServices.get(resolvedService.serviceCode);
    if (sourceService === undefined) {
      throw new Dm2016CalculationInputError(
        `D.M. 2016 service input is missing: ${resolvedService.serviceCode}`,
      );
    }
    const sourceMultiplier = sourceService.multiplier ?? "1";
    if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(sourceMultiplier)) {
      throw new Dm2016CalculationInputError(
        `Invalid multiplier for ${resolvedService.serviceCode}`,
      );
    }
    const multiplier = new CalcDecimal(sourceMultiplier);
    if (!multiplier.isFinite() || multiplier.lte(0)) {
      throw new Dm2016CalculationInputError(
        `Multiplier must be positive for ${resolvedService.serviceCode}`,
      );
    }
    const effectiveMultiplier = decimalString(
      multiplier.times(resolvedService.multiplier),
    );
    return calculateDm2016ReferenceCaseForVerification({
      z1: input.z1,
      z2Matrix: input.z2Matrix,
      selectionRules: input.selectionRules,
      destinationCode: input.destinationCode,
      serviceCode: resolvedService.serviceCode,
      valueV: resolvedService.valueV,
      ...(sourceService.inhabitants === undefined
        ? {}
        : { inhabitants: sourceService.inhabitants }),
      multiplier: effectiveMultiplier,
      interpretationMode: input.interpretationMode,
    });
  });
  const normalizedInput = Object.freeze({
    destinationCode: input.destinationCode,
    services: Object.freeze(references.map((reference) => Object.freeze({
      serviceCode: reference.resolution.serviceCode,
      valueV: reference.calculation.valueV,
      inhabitants: reference.resolution.selectorKind === "population"
        ? decimalString(new CalcDecimal(
          sourceServices.get(reference.resolution.serviceCode)!.inhabitants!,
        ))
        : null,
      multiplier: reference.calculation.multiplier,
    }))),
    interpretationMode: input.interpretationMode,
    footnoteContext: normalizeFootnoteContext(input.footnoteContext),
    footnoteResolverVersion: DM2016_FOOTNOTE_RESOLVER_VERSION,
  });
  const compensationSummary = summarizeDm2016Compensation({
    serviceResults: references.map((reference) => reference.calculation),
    bimMandatory: false,
    expensePercentage: "0",
  });
  const identity = Object.freeze({ ...input.identity });
  const inputSha256 = await sha256Hex(canonicalJson({
    ...identity,
    ...normalizedInput,
  } as unknown as JsonValue));
  const resultForHash = {
    engineVersion: DM2016_CALCULATION_VERSION,
    ...identity,
    professionalUseAllowed: false as const,
    certifiedEquoCompenso: false as const,
    releaseStatus: "local_unverified" as const,
    normalizedInput,
    inputSha256,
    footnoteResolution,
    references: Object.freeze(references),
    compensationSummary,
  } as const;
  const resultSha256 = await sha256Hex(
    canonicalJson(resultForHash as unknown as JsonValue),
  );
  return Object.freeze({ ...resultForHash, resultSha256 });
}
